/**
 * Applying an authored {@link PanelTheme}.
 *
 * A theme is data — a catalog says what the panels should look like, and the
 * host decides where to put it. These two helpers are the whole mapping:
 *
 * ```tsx
 * <div style={panelThemeVars(catalog.theme)}>
 *   <PanelHost />
 * </div>
 * ```
 *
 * **Set the variables on an ANCESTOR, not on the panel itself.** Two of them are
 * read by the modal scrim (`--fias-panel-scrim`, `--fias-panel-z`), which is the
 * panel's PARENT — variables set on the panel would never reach it, and the
 * backdrop would silently keep the default while everything else changed. An
 * ancestor of the presenter covers both.
 *
 * These are plain functions, not a `<PanelThemeProvider>`: a theme is just
 * inherited CSS custom properties, so a wrapper element already does the whole
 * job — and a provider would be a React-context dependency that the vendored
 * plugin build would have to carry for no added capability.
 *
 * ── THE VALUE GRAMMARS ARE MIRRORED HERE, ON PURPOSE ─────────────────────────
 *
 * The canonical `panelThemeSchema` in `packages/db-types/src/panel-definition.ts`
 * rejects anything but a hex/`rgb()` colour, a `px`/`rem`/`em` length and a
 * paren-free font stack, so that a theme value can never carry `url(…)` (an
 * outbound request — a tracking pixel — from every surface that draws a panel)
 * or a `;` that escapes its declaration in a renderer building a style string.
 *
 * This module re-validates every value against the same grammars rather than
 * trusting its caller, for exactly the reason spelled out on
 * `PanelRenderContext.allowResolvedUrls`: a renderer that accepted at READ time
 * what the validator forbids at WRITE time becomes the weakest link in that
 * lock. The write gate covers the one authoring path that exists today
 * (admin-curated config); this covers every other path a theme could arrive
 * through — a plugin's own DataStore, a URL parameter, a future authoring UI —
 * without either side having to know about the other. Same shape as
 * `parsePanelAction`, which mirrors the action grammar for the same reason, and
 * pinned against the canonical schema by a shared corpus in
 * `tests/architecture/panel-schema-mirror.test.ts`.
 *
 * An invalid value is DROPPED, never thrown on. A bad colour then falls back to
 * the host's own variable, which is the same degrade as a theme that never set
 * it — consistent with the renderer's rule that one malformed part of a
 * definition must not take the rest of the panel down with it.
 */
import type { CSSProperties } from 'react';

import type { PanelTheme } from './types';

/** `#rgb` / `#rgba` / `#rrggbb` / `#rrggbbaa`, or `rgb()/rgba()` with plain numbers. */
export const PANEL_THEME_COLOR_PATTERN =
  /^(?:#(?:[0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})|rgba?\(\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}\s*(?:,\s*(?:0|1|0?\.\d{1,3})\s*)?\))$/i;

/** Digits allowed before the decimal point — a `9999px` ceiling. */
export const PANEL_THEME_LENGTH_MAX_INTEGER_DIGITS = 4;

/** A non-negative CSS length in `px`, `rem` or `em`. No calc(), no var(), no %. */
export const PANEL_THEME_LENGTH_PATTERN = new RegExp(
  `^\\d{1,${PANEL_THEME_LENGTH_MAX_INTEGER_DIGITS}}(?:\\.\\d{1,3})?(?:px|rem|em)$`,
);

/**
 * A font stack: names, commas, quotes. Parentheses are absent from the charset,
 * which is what makes `url(…)` and `var(…)` unexpressible rather than merely
 * discouraged.
 */
export const PANEL_THEME_FONT_PATTERN = /^[A-Za-z0-9 '",._-]+$/;

/** Mirrored bounds — pinned against the canonical constants by the mirror test. */
export const PANEL_THEME_FONT_MAX_LENGTH = 200;
export const PANEL_THEME_AMOUNT_FORMAT_MAX_LENGTH = 24;
export const PANEL_THEME_Z_MAX = 1_000_000;

/** How a field's value is validated before it reaches a custom property. */
type PanelThemeValueKind = 'color' | 'length' | 'font' | 'integer';

interface PanelThemeFieldSpec {
  cssVar: string;
  kind: PanelThemeValueKind;
}

/**
 * Theme field → CSS custom property + value grammar. Mirrors `panelThemeSchema`'s
 * keys, and `tests/architecture/panel-schema-mirror.test.ts` pins the two key sets
 * equal — an authored field with no entry here is a value that validates, stores,
 * ships and then does nothing, which is the silent no-op the trip-wire exists to
 * catch.
 *
 * The `satisfies` is deliberately NOT `Partial<…>`: every styling field must have
 * an entry, so adding one to {@link PanelTheme} without mapping it is a
 * compile error in this package rather than a red test in another one.
 *
 * `amountFormat` is the single documented exclusion — it is not styling, it is the
 * argument to {@link panelAmountFormatter}.
 */
export const PANEL_THEME_FIELDS = {
  bg: { cssVar: '--fias-panel-bg', kind: 'color' },
  fg: { cssVar: '--fias-panel-fg', kind: 'color' },
  border: { cssVar: '--fias-panel-border', kind: 'color' },
  accent: { cssVar: '--fias-panel-accent', kind: 'color' },
  accentFg: { cssVar: '--fias-panel-accent-fg', kind: 'color' },
  scrim: { cssVar: '--fias-panel-scrim', kind: 'color' },
  font: { cssVar: '--fias-panel-font', kind: 'font' },
  radius: { cssVar: '--fias-panel-radius', kind: 'length' },
  buttonRadius: { cssVar: '--fias-panel-button-radius', kind: 'length' },
  gap: { cssVar: '--fias-panel-gap', kind: 'length' },
  fontSize: { cssVar: '--fias-panel-font-size', kind: 'length' },
  widthSmall: { cssVar: '--fias-panel-width-small', kind: 'length' },
  widthMedium: { cssVar: '--fias-panel-width-medium', kind: 'length' },
  widthLarge: { cssVar: '--fias-panel-width-large', kind: 'length' },
  z: { cssVar: '--fias-panel-z', kind: 'integer' },
} as const satisfies Record<Exclude<keyof PanelTheme, 'amountFormat'>, PanelThemeFieldSpec>;

/** Field → custom property, for callers that only need the mapping. */
export const PANEL_THEME_CSS_VARS: Readonly<Record<string, string>> = Object.fromEntries(
  Object.entries(PANEL_THEME_FIELDS).map(([field, spec]) => [field, spec.cssVar]),
);

/** Does this value satisfy the grammar its field is declared with? */
function isValidThemeValue(kind: PanelThemeValueKind, value: unknown): boolean {
  if (kind === 'integer') {
    return (
      typeof value === 'number' &&
      Number.isInteger(value) &&
      value >= 0 &&
      value <= PANEL_THEME_Z_MAX
    );
  }
  if (typeof value !== 'string' || value.length === 0) return false;
  if (kind === 'color') return PANEL_THEME_COLOR_PATTERN.test(value);
  if (kind === 'length') return PANEL_THEME_LENGTH_PATTERN.test(value);
  return value.length <= PANEL_THEME_FONT_MAX_LENGTH && PANEL_THEME_FONT_PATTERN.test(value);
}

/**
 * A `style` object setting the custom properties an authored theme specifies.
 *
 * Unset fields are OMITTED rather than written as an empty value: an empty custom
 * property overrides an inherited one with nothing, which would defeat the host
 * CSS a partial theme is supposed to leave alone. A value that fails its grammar
 * is dropped the same way — see the note at the top of this file.
 */
export function panelThemeVars(theme: PanelTheme | undefined | null): CSSProperties {
  // Built as a plain string map and cast at the boundary: `CSSProperties` has no
  // index signature for custom properties, so `--fias-panel-bg` is not an
  // assignable key. This is React's own documented escape hatch for CSS
  // variables, not a shortcut around a type error — don't "fix" it by widening
  // the return type, which would let any typo'd CSS property through.
  const style: Record<string, string> = {};
  if (!theme) return style as CSSProperties;
  for (const [field, spec] of Object.entries(PANEL_THEME_FIELDS)) {
    // Own-property only: the field list is fixed, but the THEME is caller data,
    // and `{}.constructor` is not a value anyone authored.
    if (!Object.prototype.hasOwnProperty.call(theme, field)) continue;
    const value = theme[field as keyof PanelTheme];
    if (!isValidThemeValue(spec.kind, value)) continue;
    style[spec.cssVar] = String(value);
  }
  return style as CSSProperties;
}

/**
 * Turn a theme's `amountFormat` into a `formatAmount` function for the render
 * context. Returns `undefined` when there is no usable format, so the caller can
 * pass it straight through and let the renderer's own default apply:
 *
 * ```ts
 * context={{ items, formatAmount: panelAmountFormatter(theme?.amountFormat) }}
 * ```
 *
 * A format with no `{n}`, or one longer than the authored cap, is REJECTED rather
 * than rendered literally. The write validator already requires the placeholder,
 * so reaching here without one means the value came from somewhere unvalidated —
 * and a format that silently swallows the quantity would drop the number from
 * every reward row in the catalog, which is worse than falling back to the
 * default.
 */
export function panelAmountFormatter(
  amountFormat: string | undefined | null,
): ((amount: number) => string) | undefined {
  if (typeof amountFormat !== 'string') return undefined;
  if (!amountFormat.includes('{n}')) return undefined;
  if (amountFormat.length > PANEL_THEME_AMOUNT_FORMAT_MAX_LENGTH) return undefined;
  // `split`/`join` rather than `replaceAll`: the esbuild target is ES2020 and
  // `String.prototype.replaceAll` is ES2021.
  return (amount: number) => amountFormat.split('{n}').join(String(amount));
}
